import React from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';

export const DemoNavigation = () => {
  const pageGroups = [
    {
      title: 'Public Pages',
      pages: [
        { name: 'Landing Page', path: '/', description: 'Homepage with hero and benefits' },
        { name: 'Registration', path: '/register', description: 'Multi-step account creation flow' },
        { name: 'Email Verification', path: '/verify-email', description: 'Email confirmation page with resend option' },
        { name: 'Login', path: '/login', description: 'User authentication page' },
        { name: 'Password Reset', path: '/reset-password', description: 'Password recovery flow' },
      ]
    },
    {
      title: 'Writer Pages',
      pages: [
        { name: 'Writer Onboarding', path: '/onboarding/writer', description: '3-step onboarding with profile, writing details, and preferences' },
        { name: 'Writer Profile Edit', path: '/writer/profile/edit', description: 'Edit profile with photo, bio, genre, and social links' },
        { name: 'Writer Dashboard', path: '/writer/dashboard', description: 'Dashboard with stats, recent activity, and quick actions' },
        { name: 'My Manuscripts', path: '/writer/manuscripts', description: 'Manuscript list with search, filtering, and grid/list view' },
        { name: 'Create Manuscript', path: '/writer/manuscripts/new', description: 'Form to add new manuscript with full details' },
        { name: 'Edit Manuscript', path: '/writer/manuscripts/1/edit', description: 'Edit existing manuscript with version history' },
        { name: 'Manuscript Detail', path: '/writer/manuscripts/1', description: 'View manuscript details with status management and beta reader controls' },
        { name: 'Beta Applications', path: '/writer/manuscripts/1/applications', description: 'Manage beta reader applications with accept/decline actions' },
        { name: 'Active Beta Readers', path: '/writer/beta-readers', description: 'View all active beta readers and their reading progress' },
        { name: 'Beta Feedback', path: '/writer/manuscripts/1/feedback', description: 'View and respond to beta reader feedback with ratings' },
        { name: 'Agent Directory', path: '/agents', description: 'Browse literary agents with filtering by genre and status' },
        { name: 'Agent Profile', path: '/agents/1', description: 'View agent details, MSWL, submission guidelines, and recent sales' },
        { name: 'Submit Query', path: '/writer/query/new', description: 'Submit query letter to agents with preview before sending' },
        { name: 'My Queries', path: '/writer/queries', description: 'Track all submitted queries with status and response tracking' },
        { name: 'Query Detail', path: '/writer/queries/2', description: 'View full query details, timeline, and agent responses' },
      ]
    },
    {
      title: 'Beta Reader Pages',
      pages: [
        { name: 'Reader Onboarding', path: '/onboarding/reader', description: '3-step onboarding with profile, reading preferences, and availability' },
        { name: 'Reader Profile Edit', path: '/reader/profile/edit', description: 'Edit profile with genres, reading speed, and content preferences' },
        { name: 'Reader Dashboard', path: '/reader/dashboard', description: 'Dashboard with current manuscripts, pending applications, and recommendations' },
        { name: 'Discover Manuscripts', path: '/manuscripts/discover', description: 'Browse manuscripts with genre, word count, and content filters' },
        { name: 'Manuscript Preview', path: '/manuscripts/1/preview', description: 'View manuscript details, synopsis, and first pages before applying' },
        { name: 'Apply to Read', path: '/manuscripts/1/apply', description: 'Submit application to beta read with timeline and experience details' },
        { name: 'My Applications', path: '/reader/applications', description: 'Track all applications with status filtering and withdraw options' },
        { name: 'Current Reads', path: '/reader/manuscripts', description: 'Manage active manuscripts with progress tracking and deadlines' },
        { name: 'Manuscript Reader', path: '/reader/manuscripts/1/read', description: 'Read manuscript with chapter navigation, notes, and bookmarks' },
        { name: 'Submit Feedback', path: '/reader/manuscripts/1/feedback', description: 'Provide detailed feedback with ratings and category-specific comments' },
        { name: 'Reading History', path: '/reader/history', description: 'View completed reads with stats, feedback given, and writer responses' },
      ]
    },
    {
      title: 'Agent Pages',
      pages: [
        { name: 'Agent Onboarding', path: '/onboarding/agent', description: '4-step onboarding with basic info, agency affiliation, MSWL, and preferences' },
        { name: 'Agent Profile Edit', path: '/agent/profile/edit', description: 'Edit profile with bio, MSWL, genres, submission guidelines, and query status' },
        { name: 'Agent Dashboard', path: '/agent/dashboard', description: 'Dashboard with query stats, recent queries, upcoming deadlines, and activity feed' },
        { name: 'Query Inbox', path: '/agent/queries', description: 'Searchable query inbox with filtering, bulk actions, and status management' },
        { name: 'Query Detail', path: '/agent/queries/1', description: 'View full query with letter, synopsis, first pages, and private notes' },
        { name: 'Send Response', path: '/agent/queries/1/respond', description: 'Respond to query with template system for passes and manuscript requests' },
        { name: 'Requested Manuscripts', path: '/agent/manuscripts', description: 'Track requested manuscripts with status, progress, and deadline management' },
        { name: 'Manuscript Reader', path: '/agent/manuscripts/1/read', description: 'Read manuscripts with evaluation forms, ratings, and decision options' },
        { name: 'MSWL Management', path: '/agent/mswl', description: 'Edit and publish your Manuscript Wish List with genres, wants, and deal breakers' },
      ]
    },
    {
      title: 'Agency Account Pages',
      pages: [
        { name: 'Agency Dashboard', path: '/agency/dashboard', description: 'Overview with agent roster, aggregate stats, and recent activity across the agency' },
        { name: 'Agency Profile Edit', path: '/agency/profile/edit', description: 'Edit agency information, logo, contact details, and submission guidelines' },
        { name: 'Manage Agents', path: '/agency/agents', description: 'Invite, manage, and view statistics for all agents in the agency' },
        { name: 'Agency Settings', path: '/agency/settings', description: 'Configure global preferences, submission rules, and auto-response templates' },
      ]
    },
    {
      title: 'Messaging System',
      pages: [
        { name: 'Conversations List', path: '/messages', description: 'View all message conversations with search, unread indicators, and archive options' },
        { name: 'Conversation Thread', path: '/messages/1', description: 'Read and reply to messages with archive, delete, and report options' },
        { name: 'New Message', path: '/messages/new', description: 'Start a new conversation with recipient selector and subject line' },
      ]
    },
    {
      title: 'Notifications',
      pages: [
        { name: 'Notifications Center', path: '/notifications', description: 'View all notifications with filtering, mark as read, and clear options' },
        { name: 'Notification Settings', path: '/settings/notifications', description: 'Configure email preferences, in-app notifications, digest settings, and general preferences' },
      ]
    },
    {
      title: 'Settings & Account',
      pages: [
        { name: 'Settings', path: '/settings', description: 'Unified settings page with tabs for General (account, security, privacy) and Profile (visibility, discoverability, blocked users)' },
      ]
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '60px',
        }}>
          <div style={{
            display: 'inline-block',
            backgroundColor: theme.colors.primaryLight,
            color: theme.colors.primaryDark,
            padding: '8px 16px',
            borderRadius: theme.borderRadius.pill,
            fontSize: '13px',
            fontWeight: '500',
            marginBottom: '24px',
            fontFamily: theme.fonts.monospace,
            letterSpacing: '0.05em',
          }}>
            DEMO NAVIGATION
          </div>
          <h1 style={{
            fontSize: '48px',
            fontWeight: '400',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Manuscript Match Pages
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            Explore all completed pages for the Manuscript Match platform
          </p>
        </div>

        {pageGroups.map((group, groupIndex) => (
          <div key={groupIndex} style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '400',
              marginBottom: '24px',
              color: theme.colors.textPrimary,
              paddingBottom: '12px',
              borderBottom: `2px solid ${theme.colors.border}`,
            }}>
              {group.title}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '24px',
            }}>
              {group.pages.map((page, index) => (
                <div key={index} style={{
                  backgroundColor: theme.colors.white,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.xlarge,
                  padding: '32px',
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.colors.primary,
                    fontFamily: theme.fonts.monospace,
                    marginBottom: '12px',
                  }}>
                    PAGE {index + 1}
                  </div>
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: theme.colors.textPrimary,
                  }}>
                    {page.name}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    {page.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                  }}>
                    <Button
                      variant="primary"
                      onClick={() => window.location.href = page.path}
                    >
                      View Page
                    </Button>
                    <code style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {page.path}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{
          marginTop: '40px',
          backgroundColor: theme.colors.primaryLight,
          borderRadius: theme.borderRadius.xlarge,
          padding: '40px',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '400',
            marginBottom: '16px',
            color: theme.colors.textPrimary,
          }}>
            Progress Update
          </h2>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            maxWidth: '700px',
            margin: '0 auto 24px',
          }}>
            Completed: Public pages + Writer Onboarding & Profile + Dashboard & Manuscripts + Beta Readers Management + Querying + Beta Reader Onboarding & Profile + Beta Reader Dashboard & Discovery + Beta Reader Applications & Reading + Agent Onboarding & Profile + Agent Dashboard & Queries + Agent Manuscript Management + Agency Account Pages + Messaging System + Notifications + Settings & Account sections from the site checklist.
            All pages use consistent styling based on the Manuscript Match design system.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};
